"use client"
import React, { useEffect, useRef } from 'react'

// 兼容播放 mp4 与 HLS(m3u8)：Safari 原生支持 HLS，其余浏览器动态加载 hls.js；
// ref 透传到底层 video 元素（FusionSection 的 IntersectionObserver 门控播放依赖它）
// 防下载小动作：屏蔽右键菜单 + 控件栏去下载按钮（真正的防线是 HLS 加密，这里只挡顺手牵羊）
export interface HlsVideoProps extends React.VideoHTMLAttributes<HTMLVideoElement> {
    src: string
}

const HlsVideo = React.forwardRef<HTMLVideoElement, HlsVideoProps>(function HlsVideo({ src, onContextMenu, controlsList, ...props }, ref) {
    const innerRef = useRef<HTMLVideoElement | null>(null)

    function setRefs(el: HTMLVideoElement | null) {
        innerRef.current = el
        if (typeof ref === 'function') ref(el)
        else if (ref) (ref as React.MutableRefObject<HTMLVideoElement | null>).current = el
    }

    const isHls = /\.m3u8($|\?)/.test(src || '')

    useEffect(() => {
        const v = innerRef.current
        if (!v || !src || !isHls) return
        // Safari 原生 HLS
        if (v.canPlayType('application/vnd.apple.mpegurl')) {
            v.src = src
            return
        }
        let hls: import('hls.js').default | null = null
        let cancelled = false
        import('hls.js').then(({ default: Hls }) => {
            if (cancelled || !innerRef.current) return
            if (!Hls.isSupported()) return
            hls = new Hls()
            hls.loadSource(src)
            hls.attachMedia(innerRef.current)
        }).catch(() => { })
        return () => {
            cancelled = true
            hls?.destroy()
        }
    }, [src, isHls])

    // mp4 直接走 src 属性；HLS 由上面的 effect 接管（Safari 也在 effect 里设 src）
    return (
        <video
            ref={setRefs}
            src={isHls ? undefined : src}
            controlsList={[controlsList, 'nodownload'].filter(Boolean).join(' ')}
            draggable={false}
            onContextMenu={(e) => { e.preventDefault(); onContextMenu?.(e) }}
            {...props}
        />
    )
})

export default HlsVideo
